"use client";
import { Button } from "@/components-shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components-shared/ui/dialog";
import React, { useEffect, useState } from "react";
import _360deg from "@/public/images/360deg.svg";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { CircleAlert } from "lucide-react";
import CircularProgress from "@/components-shared/CircularProgress";

const View360 = ({ view360url }: { view360url?: string | null }) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations();
  const [err, setErr] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    if (open) setLoading(true);
  }, [open]);

  return view360url ? (
    <Dialog {...{ open, onOpenChange: setOpen }}>
      <DialogTrigger asChild>
        <Button
          aria-label="360 view button"
          size={"icon"}
          className="p-0 bg-black/50 rounded-full size-7 md:size-8 lg:size-10"
        >
          <Image
            src={_360deg}
            alt="360deg"
            className="size-5 md:size-6 lg:size-7"
          />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-2 pt-4">
        <DialogHeader>
          <DialogTitle
            className={cn("text-xl font-semibold leading-none tracking-tight")}
          >
            {t("unitDetails.view360")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-center h-[550px] items-center size-full overflow-hidden rounded-xl">
          {/* Show loader if loading */}
          {loading && !err && <CircularProgress size="lg" />}

          {/* Show warning icon if there's an error */}
          {err && <CircleAlert className="text-gray-500 !size-32" />}

          <iframe
            className={cn("h-full w-full", {
              hidden: loading || err,
            })}
            src={view360url}
            allowFullScreen
            allow="xr-spatial-tracking"
            onLoad={() => setLoading(false)} // Update loading state on success
            onError={() => {
              setErr(true); // Update error state
              setLoading(false); // Stop the loader
            }}
          ></iframe>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;
};

export default View360;
